import { Link } from 'react-router-dom';
import { HeartPulse, ChevronRight, PawPrint } from 'lucide-react';
import { usePets } from '../../contexts/PetsContext';
import { useHealth } from '../../contexts/HealthContext';
import { usePetPhoto } from '../../hooks/usePetPhoto';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import { ROUTES } from '../../config/constants';
import { formatTimeAgo } from '../../lib/format';

/**
 * Row de una mascota en HealthPage. Extraído porque cada row necesita su
 * propio usePetPhoto (no se puede llamar dentro de .map() del padre).
 */
const PetHealthRow = ({ pet, recordCount, lastMs }) => {
  const { url: photoUrl } = usePetPhoto(pet);
  const detail = ROUTES.PET_HEALTH.replace(':petId', pet.id);
  return (
    <Link
      to={detail}
      className="block focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-2xl"
    >
      <Card className="hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3">
          <Avatar src={photoUrl} name={pet.name} size="md" />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900">{pet.name}</h3>
            <p className="text-sm text-slate-500">
              {recordCount === 0
                ? 'Sin observaciones todavía'
                : `${recordCount} observación${recordCount === 1 ? '' : 'es'}`}
              {lastMs && (
                <span className="text-slate-400">
                  {' · última '}{formatTimeAgo(lastMs)}
                </span>
              )}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
        </div>
      </Card>
    </Link>
  );
};

/**
 * Vista principal del módulo Salud.
 * Lista las mascotas con badge "última observación: hace X".
 * Tocar una pet abre su timeline (PetHealthPage).
 */

const HealthPage = () => {
  const { pets, status: petsStatus } = usePets();
  const { lastRecordByPet, records, status: healthStatus } = useHealth();

  const isLoading = petsStatus === 'loading' || healthStatus === 'loading';
  const noPets = petsStatus === 'ready' && pets.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-2">
        <HeartPulse className="h-6 w-6 text-rose-700" aria-hidden="true" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Salud</h1>
          <p className="text-sm text-slate-500">
            Peso, vacunas, antiparasitarios y observaciones de cada mascota.
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <div className="h-20 animate-pulse rounded-2xl bg-slate-100" />
          <div className="h-20 animate-pulse rounded-2xl bg-slate-100" />
        </div>
      )}

      {noPets && (
        <Card>
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <PawPrint className="h-10 w-10 text-slate-300" aria-hidden="true" />
            <div>
              <p className="font-medium text-slate-800">No hay mascotas todavía</p>
              <p className="mt-1 text-sm text-slate-500">
                Creá una mascota para empezar a llevar el registro de su salud.
              </p>
            </div>
            <Button as={Link} to={ROUTES.PET_NEW} variant="primary">
              Crear mascota
            </Button>
          </div>
        </Card>
      )}

      {!isLoading && !noPets && (
        <div className="flex flex-col gap-2">
          {pets.map((pet) => {
            const lastMs = lastRecordByPet.get(pet.id);
            const recordCount = records.filter((r) => r.petId === pet.id).length;
            return (
              <PetHealthRow
                key={pet.id}
                pet={pet}
                recordCount={recordCount}
                lastMs={lastMs}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HealthPage;
